import { Capacitor } from '@capacitor/core';
import { ScreenBrightness } from '@capacitor-community/screen-brightness';
import { Divider, Stack } from '@mui/material';
import { useEffect, useState } from 'react';
import { useEffectOnce } from 'react-use';

import { AccountModel } from '@portal-clients/account';

import { useAccountToken, usePortalConfiguration } from '@frontend/utils';

import { AccountDetailsBalance } from './account-details-balance.component';
import { AccountDetailsInfo } from './account-details-info.component';
import { AccountDetailsQr } from './account-details-qr.component';

export interface AccountDetailsDrawerContentProps {
  open: boolean;
  account: AccountModel;
}

export const AccountDetailsDrawerContent = (
  props: AccountDetailsDrawerContentProps,
) => {
  const { config } = usePortalConfiguration();
  const { tokens } = useAccountToken();
  const qrToken = config.features?.qrCodeIdentification?.enabled
    ? tokens?.[props.account.id]
    : undefined;

  const [prevBrightnessLevel, setPrevBrightnessLevel] = useState<
    number | undefined
  >(undefined);

  useEffectOnce(() => {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    ScreenBrightness.getBrightness()
      .then(({ brightness }) => {
        setPrevBrightnessLevel(brightness);
      })
      .catch(console.warn);
  });

  useEffect(() => {
    if (!Capacitor.isNativePlatform() || prevBrightnessLevel === undefined) {
      return;
    }

    if (!props.open) {
      // If brightness changes fails we ignore the errors. There is not much
      // we can do if we are not allowed to change it.
      ScreenBrightness.setBrightness({ brightness: prevBrightnessLevel }).catch(
        console.warn,
      );
      return;
    }

    ScreenBrightness.setBrightness({ brightness: 1 }).catch(console.warn);
  }, [props.open, prevBrightnessLevel]);

  return (
    <Stack spacing={2}>
      <Stack spacing={6}>
        {qrToken && <AccountDetailsQr token={qrToken} />}
        <Stack spacing={2}>
          {qrToken && <Divider />}
          {props.account.type !== 'DIRECT' && (
            <AccountDetailsBalance account={props.account} />
          )}
          <AccountDetailsInfo account={props.account} />
        </Stack>
      </Stack>
    </Stack>
  );
};
